import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const REFERRAL_STORAGE_KEY = 'qg_referral';

export function useReferral() {
  const [searchParams] = useSearchParams();
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referrerAddress, setReferrerAddress] = useState<string | null>(null);

  // Load referral code from URL or localStorage
  useEffect(() => {
    // Check URL parameters first
    const refFromUrl = searchParams.get('ref');
    
    if (refFromUrl) {
      // If found in URL, save to localStorage
      localStorage.setItem(REFERRAL_STORAGE_KEY, refFromUrl);
      setReferralCode(refFromUrl);
    } else {
      // Try to load from localStorage
      const savedRef = localStorage.getItem(REFERRAL_STORAGE_KEY);
      if (savedRef) {
        setReferralCode(savedRef);
      }
    }
  }, [searchParams]);

  // Resolve referral code to address
  useEffect(() => {
    if (!referralCode) {
      setReferrerAddress(null);
      return;
    }

    // Check if the referral code is already an address (0x...)
    if (referralCode.startsWith('0x') && referralCode.length === 42) {
      setReferrerAddress(referralCode);
    } else {
      // Try to resolve from your referrals mapping
      import('../config/referrals').then(({ referrals }) => {
        const address = referrals[referralCode];
        if (address) {
          setReferrerAddress(address);
        } else {
          // If not found, set to null or a default referrer
          setReferrerAddress(null);
        }
      }).catch(err => {
        // Silent fail - don't log to console
        setReferrerAddress(null);
      });
    }
  }, [referralCode]);

  // Generate referral link for current user
  const generateReferralLink = (userAddress: string) => {
    // Use the full address for proper database matching with Supabase
    return `${window.location.origin}/?ref=${userAddress}`;
  };

  // Check if referrer address matches the payment chain
  const getReferrerAddressForChain = (chain: 'evm' | 'sol') => {
    if (!referrerAddress) return null;
    
    // For EVM chains (ETH, BSC)
    if (chain === 'evm') {
      return referrerAddress.startsWith('0x') ? referrerAddress : null;
    }
    
    // For Solana
    if (chain === 'sol') {
      return !referrerAddress.startsWith('0x') ? referrerAddress : null;
    }
    
    return null;
  };

  return {
    referralCode,
    referrerAddress,
    generateReferralLink,
    getReferrerAddressForChain,
  };
} 